import { useMemo } from 'react';
import { useBahasa } from '../i18n';

/**
 * Kendali skala timeline — pengganti sepasang tombol `−` / `+` (8 Sep 2026).
 *
 * ## Kenapa yang lama membingungkan
 *
 * Dilaporkan lapangan: *"mau di singlecam atau multicam perbesaran skala
 * sekarang ditandai dengan tanda + dan −. Itu gw sebagai pengguna awam bingung
 * lho."* Dan itu bukan soal ikon yang kekecilan. `+` di atas sebuah timeline
 * punya dua bacaan yang sama-sama masuk akal:
 *
 * - "tambah waktu" → jendela jadi **lebih lebar**, atau
 * - "perbesar" → jendela jadi **lebih sempit**.
 *
 * Yang berlaku bacaan kedua, jadi menekan `+` justru **mengurangi** jam yang
 * terlihat. Ditambah dua kekurangan yang membuat tebakannya tidak pernah
 * terkoreksi: tidak ada angka yang menyebut skala yang sedang berlaku, dan
 * tidak ada cara melompat ke skala tertentu selain menekan berulang.
 *
 * ## Yang menyelesaikannya
 *
 * 1. **Angkanya selalu terlihat.** Pertanyaan sebenarnya bukan "tombol mana
 *    yang memperbesar" melainkan "sekarang saya sedang melihat berapa lama".
 *    Begitu jawabannya tertulis, arah tombol berhenti jadi tebakan: ditekan
 *    sekali, angkanya berubah, selesai.
 * 2. **Tanda `+`/`−` menempel di kaca pembesar**, bukan berdiri sendiri. Yang
 *    dibaca jadi "perbesar", bukan "tambah jam" — ambiguitasnya hilang karena
 *    ikonnya yang membawa arti.
 * 3. **Slidernya bertakik.** Geser bebas menghasilkan "4 jam 17 menit", dan
 *    seluruh timeline ini dibangun dengan aturan sebaliknya — penggarisnya pun
 *    memilih label dari tangga yang bulat (`TANGGA_MS`). Bertakik juga berarti
 *    satu gerakan cukup untuk sampai ke skala yang dimau.
 * 4. **Kiri rapat, kanan renggang**, searah dengan sumbu waktunya sendiri.
 *
 * Roda tetikus dan cubitan **tidak** dibuat bertakik: di situ tangan sudah
 * menentukan sendiri, dan menjepretkannya ke takik terdekat akan terasa seperti
 * kendali yang melawan. Yang terjadi kalau nilainya jatuh di antara dua takik:
 * gagangnya duduk di takik terdekat, angkanya tetap menyebut yang sebenarnya.
 */

const MENIT = 60_000;
const JAM = 3_600_000;
const HARI = 86_400_000;

/**
 * Takik yang boleh dipilih.
 *
 * Dua belas, dan bukan satu per kelipatan: yang dicari orang di timeline
 * pengawasan adalah "semenit", "seperempat jam", "sehari" — bukan tangga
 * geometris yang rapi tapi tidak pernah disebut siapa pun. Takik yang lebih
 * besar daripada sumbunya sendiri disaring keluar saat render.
 */
const TAKIK = [
  1 * MENIT,
  5 * MENIT,
  15 * MENIT,
  30 * MENIT,
  1 * JAM,
  3 * JAM,
  6 * JAM,
  12 * JAM,
  1 * HARI,
  3 * HARI,
  7 * HARI,
  30 * HARI,
];

/**
 * "6 jam", "15 menit", "7 hari".
 *
 * Satu desimal untuk nilai yang tidak jatuh di takik (hasil roda tetikus atau
 * cubitan) — "5,5 jam" jujur, "5 jam" bohong, dan "5 jam 27 menit 13 detik"
 * adalah ketepatan yang tidak dibutuhkan siapa pun untuk membaca skala.
 */
export function teksRentang(
  ms: number,
  bahasa: string,
  t: (teks: string, vars?: Record<string, string | number>) => string,
): string {
  const angka = (n: number) =>
    n.toLocaleString(bahasa === 'id' ? 'id-ID' : 'en-GB', { maximumFractionDigits: 1 });

  if (ms < JAM) {
    const n = Math.max(1, Math.round(ms / MENIT));
    return n === 1 ? t('1 minute') : t('{n} minutes', { n: angka(n) });
  }
  if (ms < HARI) {
    const n = ms / JAM;
    return Math.abs(n - 1) < 0.05 ? t('1 hour') : t('{n} hours', { n: angka(n) });
  }
  const n = ms / HARI;
  return Math.abs(n - 1) < 0.05 ? t('1 day') : t('{n} days', { n: angka(n) });
}

interface Props {
  /** Lebar jendela yang sedang terlihat. */
  rentangMs: number;
  /** Lebar sumbu paling luar — takik yang melewatinya tidak ditawarkan. */
  penuhMs: number;
  /** Jendela tersempit yang diizinkan pemanggil. Bawaan satu menit. */
  lantaiMs?: number;
  /** Setel lebar jendela ke sekian milidetik. Pusat & penjepitnya milik pemanggil. */
  onRentang: (ms: number) => void;
  /** Kembali ke seluruh rentang yang dimuat. */
  onMuatPenuh: () => void;
}

export function KendaliSkala({ rentangMs, penuhMs, lantaiMs = MENIT, onRentang, onMuatPenuh }: Props) {
  const { t, bahasa } = useBahasa();

  const takik = useMemo(() => {
    const dalam = TAKIK.filter((v) => v >= lantaiMs && v <= penuhMs);
    // Ujung kanan selalu bisa dicapai: tanpa ini, rentang muat 45 menit membuat
    // takik terbesar 30 menit, dan sumbu tidak pernah bisa kembali utuh lewat
    // slider — padahal itu gerakan yang paling sering dicari sesudah zoom.
    if (dalam.length === 0 || dalam[dalam.length - 1]! < penuhMs) dalam.push(penuhMs);
    if (dalam[0]! > lantaiMs) dalam.unshift(lantaiMs);
    return dalam;
  }, [lantaiMs, penuhMs]);

  /**
   * Takik terdekat, diukur pada skala **logaritmik**.
   *
   * Zoom itu perkalian, bukan penjumlahan: dari 6 jam, "satu langkah" ke 3 jam
   * dan ke 12 jam sama jauhnya bagi mata, walau selisih detiknya beda dua kali
   * lipat. Mengukur kedekatan secara linier akan membuat gagang slider menempel
   * di takik-takik besar dan hampir tidak pernah menyentuh yang kecil.
   */
  const indeks = useMemo(() => {
    let terbaik = 0;
    let jarak = Infinity;
    for (let i = 0; i < takik.length; i++) {
      const d = Math.abs(Math.log(takik[i]! / Math.max(1, rentangMs)));
      if (d < jarak) {
        jarak = d;
        terbaik = i;
      }
    }
    return terbaik;
  }, [takik, rentangMs]);

  const teks = teksRentang(rentangMs, bahasa, t);
  const pilih = (i: number) => {
    const v = takik[Math.min(takik.length - 1, Math.max(0, i))];
    if (v !== undefined) onRentang(v);
  };

  return (
    <div className="flex items-center gap-1.5">
      <span className="hidden text-muted sm:inline">{t('Scale')}</span>

      <button
        type="button"
        onClick={() => pilih(indeks - 1)}
        disabled={indeks === 0}
        aria-label={t('Zoom in')}
        title={t('Zoom in')}
        className="flex h-9 w-9 items-center justify-center rounded-md border border-line hover:bg-subtle disabled:opacity-30"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="m20 20-4.5-4.5M10.5 7.5v6M7.5 10.5h6" />
        </svg>
      </button>

      {/* Slider bawaan browser, bukan yang digambar sendiri: ia sudah bisa
          dipegang papan ketik (panah, Home/End), sudah punya target sentuh yang
          benar di Android maupun iOS, dan sudah diumumkan pembaca layar sebagai
          slider. `aria-valuetext` menggantikan angka indeksnya — "6" tidak
          berarti apa-apa, "6 jam" berarti segalanya. */}
      <input
        type="range"
        min={0}
        max={takik.length - 1}
        step={1}
        value={indeks}
        onChange={(e) => pilih(Number(e.target.value))}
        aria-label={t('Timeline scale')}
        aria-valuetext={teks}
        style={{ accentColor: 'var(--color-accent)' }}
        className="h-9 w-24 cursor-pointer sm:w-36"
      />

      <button
        type="button"
        onClick={() => pilih(indeks + 1)}
        disabled={indeks === takik.length - 1}
        aria-label={t('Zoom out')}
        title={t('Zoom out')}
        className="flex h-9 w-9 items-center justify-center rounded-md border border-line hover:bg-subtle disabled:opacity-30"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="m20 20-4.5-4.5M7.5 10.5h6" />
        </svg>
      </button>

      {/* Angka skala. Lebarnya dipatok supaya kendali di sebelahnya tidak
          bergeser tiap kali teksnya berganti panjang — bilah yang bergoyang
          sendiri saat di-zoom terbaca sebagai layar yang belum selesai dimuat. */}
      <span className="min-w-[4.5rem] tabular-nums text-text" aria-hidden="true">
        {teks}
      </span>

      <button
        type="button"
        onClick={onMuatPenuh}
        aria-label={t('Fit range')}
        title={t('Fit range')}
        className="flex h-9 w-9 items-center justify-center rounded-md border border-line hover:bg-subtle"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 9V5h4M20 9V5h-4M4 15v4h4M20 15v4h-4" />
        </svg>
      </button>
    </div>
  );
}
